// Vehicle compliance documents tab — RC, Insurance, PUC, Fitness, Permit, Road Tax
// AI-extracted fields, expiry tracking, view/download/delete.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Surface, Badge, Button } from "@/components/ui-kit";
import { UploadSimple, FileText, Trash, Sparkle, ArrowsClockwise } from "@phosphor-icons/react";
import { formatDate, daysUntil } from "@/lib/format";

export const COMPLIANCE_TYPES = [
    { key: "insurance", label: "Insurance" },
    { key: "puc", label: "PUC" },
    { key: "rc", label: "Registration (RC)" },
    { key: "fitness", label: "Fitness Certificate" },
    { key: "permit", label: "Permit" },
    { key: "road_tax", label: "Road Tax" },
];

export default function ComplianceDocsPanel({ vehicleId }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeType, setActiveType] = useState("insurance");

    const load = async () => {
        const r = await api.get(`/compliance-documents?vehicle_id=${vehicleId}`);
        setDocs(r.data);
    };

    useEffect(() => { load(); }, [vehicleId]);

    const upload = async (file, doc_type) => {
        const fd = new FormData(); fd.append("file", file);
        const tid = toast.loading("Uploading & extracting fields with AI…");
        setLoading(true);
        try {
            const r = await api.post(`/compliance-documents?doc_type=${doc_type}&vehicle_id=${vehicleId}`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(r.data.ai_status === "ok" ? "AI extracted fields" : "Uploaded (AI partial)", { id: tid });
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: tid }); }
        finally { setLoading(false); }
    };

    const reextract = async (id) => {
        const tid = toast.loading("Re-running AI extraction…");
        try {
            await api.post(`/compliance-documents/${id}/reextract`);
            toast.success("Re-extracted", { id: tid }); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: tid }); }
    };

    const del = async (id) => {
        if (!window.confirm("Delete document?")) return;
        await api.delete(`/compliance-documents/${id}`); load();
    };

    const today = new Date();
    const docsByType = (t) => docs.filter((d) => d.doc_type === t);
    const latestExpiry = (t) => {
        const xs = docsByType(t).map((d) => d.expiry_date).filter(Boolean).sort();
        return xs[xs.length - 1];
    };

    const expiryBadge = (date) => {
        if (!date) return <Badge variant="cancelled">NOT SET</Badge>;
        const days = daysUntil(date);
        if (days < 0) return <Badge variant="critical">EXPIRED · {-days}d</Badge>;
        if (days <= 30) return <Badge variant="warning">DUE · {days}d</Badge>;
        return <Badge variant="healthy">OK · {days}d</Badge>;
    };

    return (
        <Surface className="p-6">
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="label-mono">COMPLIANCE</div>
                    <h3 className="font-display text-lg font-semibold">Documents & Renewals</h3>
                </div>
                <div className="flex items-center gap-2 text-xs text-fleet-inkSoft">
                    <Sparkle size={14} className="text-fleet-accent" />
                    Powered by Claude Sonnet 4.5 vision
                </div>
            </div>

            {/* Compliance summary grid */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                {COMPLIANCE_TYPES.map((t) => {
                    const exp = latestExpiry(t.key);
                    const docCount = docsByType(t.key).length;
                    return (
                        <button
                            key={t.key}
                            onClick={() => setActiveType(t.key)}
                            className={`text-left border rounded-md p-4 transition-colors ${activeType === t.key ? "border-fleet-ink bg-slate-50" : "border-fleet-border hover:border-slate-400"}`}
                            data-testid={`compliance-tile-${t.key}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="label-mono">{t.label}</div>
                                {expiryBadge(exp)}
                            </div>
                            <div className="font-mono text-sm mt-2">{exp ? formatDate(exp) : "—"}</div>
                            <div className="text-xs text-fleet-inkMuted mt-1">{docCount} document{docCount !== 1 ? "s" : ""}</div>
                        </button>
                    );
                })}
            </div>

            {/* Active type detail */}
            <div className="border-t border-fleet-border pt-5">
                <div className="flex items-center justify-between mb-4">
                    <h4 className="font-display font-semibold">{COMPLIANCE_TYPES.find((c) => c.key === activeType)?.label}</h4>
                    <label className="cursor-pointer">
                        <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f, activeType); e.target.value = ""; }}
                            data-testid={`upload-compliance-${activeType}`} />
                        <Button as="span" disabled={loading}><UploadSimple size={16} /> {loading ? "Processing…" : "Upload"}</Button>
                    </label>
                </div>

                {docsByType(activeType).length === 0 ? (
                    <div className="border border-fleet-border rounded-md py-10 text-center text-sm text-fleet-inkSoft">
                        No {COMPLIANCE_TYPES.find((c) => c.key === activeType)?.label} uploaded. Upload to auto-extract fields.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {docsByType(activeType).map((d) => (
                            <div key={d.id} className="border border-fleet-border rounded-md p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <FileText size={16} className="text-fleet-inkSoft" />
                                            <div className="text-sm font-medium truncate">{d.original_filename}</div>
                                            <Badge variant={d.ai_status === "ok" ? "healthy" : "warning"}>{d.ai_status === "ok" ? "AI OK" : "AI partial"}</Badge>
                                            {expiryBadge(d.expiry_date)}
                                        </div>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2 mt-3 text-sm">
                                            <div><span className="label-mono">DOC #</span><div className="font-mono mt-0.5">{d.document_number || "—"}</div></div>
                                            <div><span className="label-mono">ISSUE</span><div className="font-mono mt-0.5">{formatDate(d.issue_date)}</div></div>
                                            <div><span className="label-mono">EXPIRY</span><div className="font-mono mt-0.5">{formatDate(d.expiry_date)}</div></div>
                                            <div><span className="label-mono">HOLDER</span><div className="font-mono mt-0.5 truncate">{d.holder_name || "—"}</div></div>
                                            {d.vehicle_reg_number && <div><span className="label-mono">REG</span><div className="font-mono mt-0.5">{d.vehicle_reg_number}</div></div>}
                                        </div>
                                        {d.additional_metadata && Object.keys(d.additional_metadata).length > 0 && (
                                            <div className="mt-3 pt-3 border-t border-dashed border-fleet-border">
                                                <div className="label-mono mb-1.5">ADDITIONAL FIELDS</div>
                                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                                                    {Object.entries(d.additional_metadata).map(([k, v]) => (
                                                        <div key={k}><span className="text-fleet-inkMuted">{k}: </span><span className="font-mono">{String(v)}</span></div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {d.ai_error && <div className="text-xs text-fleet-warning mt-2">AI note: {d.ai_error}</div>}
                                    </div>
                                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                                        <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="text-xs text-fleet-accent hover:underline">View / Download</a>
                                        <button onClick={() => reextract(d.id)} className="text-xs text-fleet-inkSoft hover:text-fleet-ink inline-flex items-center gap-1"><ArrowsClockwise size={12} /> Re-extract</button>
                                        <button onClick={() => del(d.id)} className="text-xs text-fleet-critical hover:underline inline-flex items-center gap-1"><Trash size={12} /> Delete</button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Surface>
    );
}
